import * as z from 'zod/v4';

/** Canonical Shoppex hosted-checkout brand (matches `apps/checkout/app/globals.css` `--primary`). */
export const CHECKOUT_PLATFORM_BRAND_COLOR = '#7c3aed';

/** Curated font label for Style Center controls (runtime bundles Inter via Fontsource). */
export const CHECKOUT_PLATFORM_FONT_FAMILY = 'Inter';

/** Stripe/PayPal appearance and CSS var() fallbacks when no merchant brand override exists. */
export const CHECKOUT_PLATFORM_BRAND_FALLBACK = CHECKOUT_PLATFORM_BRAND_COLOR;

/** Runtime font stack when no merchant typography override exists.
 * Geist stays in the tail: it is still bundled, and a cached document that
 * resolved `--font-geist-sans` before this default changed keeps a real face. */
export const CHECKOUT_PLATFORM_FONT_STACK =
  'var(--font-inter, Inter), Inter, var(--font-geist-sans, Geist), Geist, system-ui, sans-serif';

/** Stripe appearance fontFamily (no CSS var() — provider SDK string). */
export const CHECKOUT_PLATFORM_STRIPE_FONT_FAMILY = 'Inter, Geist, system-ui, sans-serif';

const BRAND_DERIVED_TOKEN_KEYS: Partial<Record<string, string>> = {
  'component.primaryButton.background': 'color.brand',
  'component.radio.checkedFill': 'color.brand',
};

/**
 * Platform baseline values for unset checkout tokens.
 * Used by Style Center UI, validation, and documentation — not emitted to live CSS unless explicit.
 *
 * Every neutral below is a value from the hosted checkout's tonal ladder
 * (`apps/checkout/app/globals.css`, dark appearance). The zinc palette these
 * replace was a SECOND palette: the Style Center baseline painted `#18181b` /
 * `#27272a` / `#222222` over a checkout whose own surfaces are `--surface-0`
 * … `--surface-4`, so a shop that had never opened the Style Center saw the
 * old design repainted on top of the new one.
 *
 * They are also PURE neutrals — R = G = B — for the same reason the ladder is:
 * a baseline that carries a hue is a hue painted over every merchant's brand,
 * and this file is what an untouched shop actually renders. The white alphas
 * below follow the same rule; they used to be a warm `255,252,248`, which is
 * what turned every uncustomised chip and product plate a shade of brown.
 */
export const CHECKOUT_PLATFORM_BASELINE: Partial<Record<string, string | number>> = {
  'color.brand': CHECKOUT_PLATFORM_BRAND_COLOR,
  'color.brandContrast': '#ffffff',
  // `--surface-1`: the ground a column sits on.
  'color.background': '#111111',
  // `--surface-0`: the working panel the buyer pays in.
  'color.surface': '#1b1b1b',
  // `--card`: a panel raised above that ground.
  'color.surfaceRaised': '#222222',
  'color.text': '#f5f5f5',
  'color.textMuted': '#a1a1a1',
  // `--border-hairline` on `.dark` in apps/checkout/app/globals.css. #2c2c2c
  // measured 1.19:1 against the `#1b1b1b` panel — a line the eye cannot find,
  // so every card on the dark checkout lost the one thing holding it off the
  // sheet. THIS default is what a hosted buyer actually sees: the slot layer
  // paints it with `!important`, so leaving it behind while globals.css moved
  // up would keep the dark edge invisible for every shop on the baseline.
  'color.border': '#3a3a3a',
  'color.focus': CHECKOUT_PLATFORM_BRAND_COLOR,
  'color.success': '#22c55e',
  'color.warning': '#f59e0b',
  'color.error': '#ef4444',
  'typography.fontFamily': CHECKOUT_PLATFORM_FONT_FAMILY,
  'typography.baseSize': 14,
  // 8 across the board, and the same 8 the token definitions below carry:
  // this map is read by `resolveCheckoutStyleTokenValue` while the emitted CSS
  // reads `definition.default`, so a disagreement between them means the
  // Style Center shows one radius and the buyer sees another.
  //
  // Down from 10 with `--radius-lg` in apps/checkout/app/globals.css (the
  // 2026 rework adopts the reference checkout's 8px edge), and the two have
  // to move together: the slot layer paints
  // `var(--spx-checkout-card-radius, var(--radius-lg))` with `!important`, so
  // THIS value is the one a hosted buyer actually sees. Marks under 16px keep
  // `--radius-xs` (4px) and are not governed by these tokens.
  'shape.buttonRadius': 8,
  'shape.inputRadius': 8,
  'shape.cardRadius': 8,
  'spacing.density': 'comfortable',
  'spacing.controlHeight': 48,
  'component.primaryButton.text': '#ffffff',
  'component.input.background': '#292929',
  // Moves with `color.border` above — a field edge and a card edge that drift
  // apart is exactly the pair the light baseline's comment pins.
  'component.input.border': '#3a3a3a',
  'component.input.focusRing': 'rgba(124,58,237,0.35)',
  'component.productCard.background': 'rgba(255,255,255,0.03)',
  'component.productCard.border': 'rgba(255,255,255,0.06)',
  'component.productCard.shadow': 'none',
  'component.productImage.background': 'rgba(255,255,255,0.06)',
  'component.productImage.border': 'rgba(255,255,255,0.04)',
  'component.productImage.icon': 'rgba(250,250,250,0.4)',
  'component.brandAvatar.background': 'rgba(255,255,255,0.06)',
  'component.brandAvatar.text': '#ffffff',
  'component.brandAvatar.border': 'rgba(255,255,255,0.1)',
  'component.pill.background': 'rgba(255,255,255,0.04)',
  'component.pill.border': 'rgba(255,255,255,0.08)',
  'component.pill.text': '#a1a1a1',
  'component.errorBanner.background': 'rgba(239,68,68,0.1)',
  'component.errorBanner.border': 'rgba(239,68,68,0.2)',
  'component.errorBanner.text': '#ef4444',
  'component.radio.idleRing': 'rgba(161,161,161,0.5)',
  'component.radio.checkedIcon': '#ffffff',
  'component.divider.color': 'rgba(255,255,255,0.06)',
};

export function isExplicitCheckoutStyleTokenValue(value: unknown): value is string | number {
  if (typeof value === 'number' && Number.isFinite(value)) return true;
  if (typeof value === 'string') return value.trim().length > 0;
  return false;
}

function resolveCheckoutStyleBrandDerivedTokenKey(key: string): string | undefined {
  return BRAND_DERIVED_TOKEN_KEYS[key];
}

export const checkoutStyleSurfaceValues = ['checkout', 'payment_link', 'embed'] as const;
export const checkoutStyleDensityValues = ['comfortable', 'compact'] as const;

export const checkoutStyleModeValues = ['light', 'dark', 'system'] as const;
export const checkoutStylePaymentLinkHeroPositionValues = ['top', 'side', 'background'] as const;
export const embedCloseButtonStyleValues = ['ghost', 'outlined', 'filled'] as const;
export const embedMobileLayoutValues = ['sheet', 'fullscreen', 'center'] as const;
export const embedContentVisibilityValues = ['visible', 'hidden'] as const;
export const checkoutStyleManagedAssetHostValues = ['assets.shoppex.io', 'cdn.shoppex.io', 'imagedelivery.net'] as const;

export const CheckoutStyleSurfaceSchema = z.enum(checkoutStyleSurfaceValues);
export const CheckoutStyleDensitySchema = z.enum(checkoutStyleDensityValues);
export const CheckoutStyleModeSchema = z.enum(checkoutStyleModeValues);
export const CheckoutStylePaymentLinkHeroPositionSchema = z.enum(checkoutStylePaymentLinkHeroPositionValues);
export const EmbedCloseButtonStyleSchema = z.enum(embedCloseButtonStyleValues);
export const EmbedMobileLayoutSchema = z.enum(embedMobileLayoutValues);
export const EmbedContentVisibilitySchema = z.enum(embedContentVisibilityValues);

export type CheckoutStyleSurface = z.infer<typeof CheckoutStyleSurfaceSchema>;
export type CheckoutStyleDensity = z.infer<typeof CheckoutStyleDensitySchema>;
export type CheckoutStyleMode = z.infer<typeof CheckoutStyleModeSchema>;
export type CheckoutStylePaymentLinkHeroPosition = z.infer<typeof CheckoutStylePaymentLinkHeroPositionSchema>;
export type EmbedCloseButtonStyle = z.infer<typeof EmbedCloseButtonStyleSchema>;
export type EmbedMobileLayout = z.infer<typeof EmbedMobileLayoutSchema>;
export type EmbedContentVisibility = z.infer<typeof EmbedContentVisibilitySchema>;

export const CHECKOUT_STYLE_TOKEN_SCHEMA_VERSION = 1;
export const SHOPPEX_STYLE_THEME_EXPORT_FORMAT_VERSION = 2;

export type CheckoutStyleTokenType = 'asset_url' | 'color' | 'font' | 'number' | 'select' | 'shadow';
export type CheckoutStyleTokenGroup = 'asset' | 'brand' | 'color' | 'typography' | 'shape' | 'spacing' | 'component' | 'embed';

export type CheckoutStyleCssTokenDefinition = {
  key: string;
  cssVar: `--spx-checkout-${string}` | `--spx-embed-${string}`;
  group: Exclude<CheckoutStyleTokenGroup, 'asset'>;
  type: Exclude<CheckoutStyleTokenType, 'asset_url'>;
  default: string | number;
  min?: number;
  max?: number;
  step?: number;
  unit?: 'px' | 'rem';
  allowedValues?: readonly string[];
  protected?: boolean;
  /**
   * The one surface this token still reaches, when it is not all of them. Same
   * field the asset definitions carry; the Style Center hides the control on
   * every other surface. Absent means "every surface", which is the norm.
   */
  surface?: CheckoutStyleSurface;
  /**
   * The token has no consumer left on any surface.
   *
   * IT STAYS IN THE SCHEMA. Shops carry stored themes that set it, and
   * `CheckoutStyleTokensSchema` is `.strict()` — dropping the key would make
   * every one of those themes fail validation on the next save. So the contract
   * keeps ACCEPTING the value and stops ACTING on it: the CSS variable is no
   * longer emitted (nothing reads it) and the Style Center hides the control
   * (an editor row that paints nothing is worse than no row).
   */
  deprecated?: boolean;
};

export type CheckoutStyleAssetTokenDefinition = {
  key: string;
  group: 'asset';
  type: 'asset_url' | 'number' | 'select';
  default: string | number | null;
  accept?: readonly string[];
  maxBytes?: number;
  min?: number;
  max?: number;
  step?: number;
  unit?: 'px';
  allowedValues?: readonly string[];
  surface?: CheckoutStyleSurface;
  /** See {@link CheckoutStyleCssTokenDefinition.deprecated}. */
  deprecated?: boolean;
};

export type CheckoutStyleTokenDefinition = CheckoutStyleCssTokenDefinition | CheckoutStyleAssetTokenDefinition;

/**
 * Whether the Style Center should still offer this token as a control, and
 * whether the CSS pipeline should still emit it.
 *
 * Read through a function rather than off the literal, because the definition
 * arrays are `as const` — the narrowed literal type of a token that does not
 * carry the flag has no `deprecated` property at all.
 */
export function isDeprecatedCheckoutStyleToken(definition: CheckoutStyleTokenDefinition): boolean {
  return definition.deprecated === true;
}

/**
 * Whether a token reaches the given surface. A definition with no `surface` is
 * surface-neutral, which is the norm; the field exists for the handful that
 * only do something on one of the three (`component.productCard.background` is
 * embed-only since the hosted summary row went flat).
 */
export function checkoutStyleTokenAppliesToSurface(
  definition: CheckoutStyleTokenDefinition,
  surface: CheckoutStyleSurface,
): boolean {
  return !definition.surface || definition.surface === surface;
}

export const checkoutStyleTokenDefinitions = [
  { key: 'color.brand', cssVar: '--spx-checkout-brand', group: 'brand', type: 'color', default: CHECKOUT_PLATFORM_BRAND_COLOR },
  { key: 'color.brandContrast', cssVar: '--spx-checkout-brand-contrast', group: 'brand', type: 'color', default: '#ffffff', protected: true },
  // Empty default → CSS variable is omitted by createCheckoutStyleCssVariables,
  // so the layout's `var(--spx-checkout-bg, fallback)` resolves to the original
  // Tailwind/ambient-gradient fallback for shops that haven't customised the
  // background. Setting an explicit hex would clobber the ambient gradient and
  // the right-panel `--surface-2` accent for every default-theme checkout.
  { key: 'color.background', cssVar: '--spx-checkout-bg', group: 'color', type: 'color', default: '' },
  // The four neutrals below are the DARK appearance of the hosted checkout's
  // tonal ladder (`apps/checkout/app/globals.css`); the light values sit in
  // CHECKOUT_PLATFORM_BASELINE_LIGHT_DEFAULTS. They are materialised into the
  // live checkout root, so they are what a shop with no Style Center theme
  // actually renders — which is why each one has to name the SAME rung the
  // component paints, not a palette of its own.
  //   surface       → `--surface-0`, the working panel the buyer pays in
  //   surfaceRaised → `--card`, a panel raised above that panel
  //                   (the provider widget frame and its loading skeleton)
  //   text          → `--foreground`
  //   textMuted     → `--muted-foreground`
  //   border        → `--border`
  { key: 'color.surface', cssVar: '--spx-checkout-surface', group: 'color', type: 'color', default: '#1b1b1b' },
  { key: 'color.surfaceRaised', cssVar: '--spx-checkout-surface-raised', group: 'color', type: 'color', default: '#222222' },
  { key: 'color.text', cssVar: '--spx-checkout-text', group: 'color', type: 'color', default: '#f5f5f5', protected: true },
  { key: 'color.textMuted', cssVar: '--spx-checkout-text-muted', group: 'color', type: 'color', default: '#a1a1a1' },
  // Never '': the border token feeds runtime-style fallbacks for inputs,
  // provider widgets and buttons, and an unset value turns those slots'
  // `var(--spx-checkout-border, …)` chains loose. The hosted checkout draws
  // almost no hairlines any more — the slots that still do read this, the rest
  // resolve their border to `transparent` in the system CSS.
  { key: 'color.border', cssVar: '--spx-checkout-border', group: 'color', type: 'color', default: '#3a3a3a' },
  { key: 'color.focus', cssVar: '--spx-checkout-focus', group: 'color', type: 'color', default: CHECKOUT_PLATFORM_BRAND_COLOR, protected: true },
  { key: 'color.success', cssVar: '--spx-checkout-success', group: 'color', type: 'color', default: '#22c55e', protected: true },
  { key: 'color.warning', cssVar: '--spx-checkout-warning', group: 'color', type: 'color', default: '#f59e0b', protected: true },
  { key: 'color.error', cssVar: '--spx-checkout-error', group: 'color', type: 'color', default: '#ef4444', protected: true },
  { key: 'typography.fontFamily', cssVar: '--spx-checkout-font', group: 'typography', type: 'font', default: CHECKOUT_PLATFORM_FONT_FAMILY },
  // Optional Google Fonts family. Empty default → CSS variable is omitted
  // so the curated fontFamily wins. When set, the storefront also injects
  // a <link rel="stylesheet"> to fonts.googleapis.com so the family is
  // actually loaded.
  { key: 'typography.googleFontFamily', cssVar: '--spx-checkout-google-font', group: 'typography', type: 'font', default: '' },
  { key: 'typography.baseSize', cssVar: '--spx-checkout-font-size', group: 'typography', type: 'number', default: 14, min: 12, max: 18, step: 1, unit: 'px' },
  // ALL THREE ARE 8, and they have to be — this is one edge, stated in three
  // places, and the whole reason it is worth a comment is that the three places
  // are read by three different renderers:
  //   1. these defaults, materialised into the live checkout root (the `shape`
  //      group is a foundation group, so an untouched shop still gets them);
  //   2. `--radius-sm` / `--radius-lg` in apps/checkout/app/globals.css, which
  //      every component class paints from, both 0.5rem;
  //   3. the Stripe Appearance object (`buildStripeAppearanceFromCheckoutStyle`),
  //      which cannot resolve `var()` and is handed these same values as plain
  //      strings for `borderRadius` and the `.Block` / `.AccordionItem` rules.
  // A disagreement between any two of them is visible as a card inside the
  // provider iframe rounded differently from the card around it. Pinned by
  // `__tests__/checkout-radius-chain.test.ts` in apps/checkout.
  { key: 'shape.buttonRadius', cssVar: '--spx-checkout-button-radius', group: 'shape', type: 'number', default: 8, min: 0, max: 24, step: 1, unit: 'px' },
  { key: 'shape.inputRadius', cssVar: '--spx-checkout-input-radius', group: 'shape', type: 'number', default: 8, min: 0, max: 24, step: 1, unit: 'px' },
  { key: 'shape.cardRadius', cssVar: '--spx-checkout-card-radius', group: 'shape', type: 'number', default: 8, min: 0, max: 28, step: 1, unit: 'px' },
  {
    key: 'spacing.density',
    cssVar: '--spx-checkout-density',
    group: 'spacing',
    type: 'select',
    default: 'comfortable',
    allowedValues: checkoutStyleDensityValues,
  },
  { key: 'spacing.controlHeight', cssVar: '--spx-checkout-control-height', group: 'spacing', type: 'number', default: 48, min: 36, max: 56, step: 2, unit: 'px' },
  { key: 'component.primaryButton.background', cssVar: '--spx-checkout-button-bg', group: 'component', type: 'color', default: '', protected: true },
  { key: 'component.primaryButton.text', cssVar: '--spx-checkout-button-text', group: 'component', type: 'color', default: '#ffffff', protected: true },
  { key: 'component.input.background', cssVar: '--spx-checkout-input-bg', group: 'component', type: 'color', default: '#292929' },
  { key: 'component.input.border', cssVar: '--spx-checkout-input-border', group: 'component', type: 'color', default: '#3a3a3a' },
  { key: 'component.input.focusRing', cssVar: '--spx-checkout-input-focus-ring', group: 'component', type: 'color', default: '' },
  // Default '' is filtered out by the preview-iframe normaliser, so the
  // CSS fallback chain on [data-spx-slot="summary.panel"] resolves to
  // --spx-checkout-bg when no merchant override is set. A merchant who
  // only edits color.background therefore sees the aside follow that
  // change without an explicit summary override.
  { key: 'component.summary.background', cssVar: '--spx-checkout-summary-bg', group: 'component', type: 'color', default: '' },
  { key: 'component.paymentMethod.background', cssVar: '--spx-checkout-payment-method-bg', group: 'component', type: 'color', default: '' },
  { key: 'component.paymentMethod.cardBackground', cssVar: '--spx-checkout-payment-method-card-bg', group: 'component', type: 'color', default: '' },
  { key: 'component.paymentMethod.border', cssVar: '--spx-checkout-payment-method-border', group: 'component', type: 'color', default: '' },
  { key: 'component.paymentMethod.selectedBackground', cssVar: '--spx-checkout-payment-method-selected-bg', group: 'component', type: 'color', default: '' },
  { key: 'component.checkoutHeader.background', cssVar: '--spx-checkout-embed-header-bg', group: 'component', type: 'color', default: '' },
  { key: 'component.checkoutHeader.text', cssVar: '--spx-checkout-embed-header-text', group: 'component', type: 'color', default: '' },
  // THE PRODUCT CARD IS GONE FROM THE HOSTED CHECKOUT, and these three tokens
  // are what is left of it.
  //
  // The 2026 rework replaced the boxed summary line item with a FLAT ROW
  // (`data-spx-slot="product.line"`, apps/checkout/components/checkout/
  // product-list.tsx). A flat row has no fill of its own, no edge and no cast —
  // that is the design, not an omission — so there is nothing on the hosted or
  // payment-link surface for a border or a shadow token to land on. They are
  // NOT re-pointed at `product.line`: handing a merchant a border control for a
  // row that must not have a border is a control that can only make the page
  // worse, and the slot layer would paint it with `!important` where no call
  // site could undo it.
  //
  // `background` is the exception and stays LIVE: the EMBED surface reads
  // `--spx-checkout-product-card-bg` in `applyCheckoutStyleToEmbedStyles`
  // (apps/checkout/lib/checkout-style.tsx), where it is the card fill AND the
  // input to the embed's light/dark inference. It is marked `surface: 'embed'`
  // so the Style Center offers it where it still does something, and it keeps
  // being emitted for every stored theme.
  { key: 'component.productCard.background', cssVar: '--spx-checkout-product-card-bg', group: 'component', type: 'color', default: 'rgba(255,255,255,0.03)', surface: 'embed' },
  { key: 'component.productCard.border', cssVar: '--spx-checkout-product-card-border', group: 'component', type: 'color', default: 'rgba(255,255,255,0.06)', deprecated: true },
  { key: 'component.productCard.shadow', cssVar: '--spx-checkout-product-card-shadow', group: 'component', type: 'shadow', default: 'none', deprecated: true },
  { key: 'component.productImage.background', cssVar: '--spx-checkout-product-image-bg', group: 'component', type: 'color', default: 'rgba(255,255,255,0.06)' },
  { key: 'component.productImage.border', cssVar: '--spx-checkout-product-image-border', group: 'component', type: 'color', default: 'rgba(255,255,255,0.04)' },
  { key: 'component.productImage.icon', cssVar: '--spx-checkout-product-image-icon', group: 'component', type: 'color', default: 'rgba(250,250,250,0.4)' },
  { key: 'component.brandAvatar.background', cssVar: '--spx-checkout-brand-avatar-bg', group: 'component', type: 'color', default: 'rgba(255,255,255,0.06)' },
  { key: 'component.brandAvatar.text', cssVar: '--spx-checkout-brand-avatar-text', group: 'component', type: 'color', default: '#ffffff' },
  { key: 'component.brandAvatar.border', cssVar: '--spx-checkout-brand-avatar-border', group: 'component', type: 'color', default: 'rgba(255,255,255,0.1)' },
  { key: 'component.pill.background', cssVar: '--spx-checkout-pill-bg', group: 'component', type: 'color', default: 'rgba(255,255,255,0.04)' },
  { key: 'component.pill.border', cssVar: '--spx-checkout-pill-border', group: 'component', type: 'color', default: 'rgba(255,255,255,0.08)' },
  { key: 'component.pill.text', cssVar: '--spx-checkout-pill-text', group: 'component', type: 'color', default: '#a1a1a1' },
  { key: 'component.errorBanner.background', cssVar: '--spx-checkout-error-bg', group: 'component', type: 'color', default: 'rgba(239,68,68,0.1)' },
  { key: 'component.errorBanner.border', cssVar: '--spx-checkout-error-border', group: 'component', type: 'color', default: 'rgba(239,68,68,0.2)' },
  { key: 'component.errorBanner.text', cssVar: '--spx-checkout-error-text', group: 'component', type: 'color', default: '#ef4444' },
  { key: 'component.warningBanner.background', cssVar: '--spx-checkout-warning-bg', group: 'component', type: 'color', default: '' },
  { key: 'component.warningBanner.border', cssVar: '--spx-checkout-warning-border', group: 'component', type: 'color', default: '' },
  { key: 'component.warningBanner.text', cssVar: '--spx-checkout-warning-text', group: 'component', type: 'color', default: '' },
  { key: 'component.radio.idleRing', cssVar: '--spx-checkout-radio-idle-ring', group: 'component', type: 'color', default: 'rgba(161,161,161,0.5)' },
  { key: 'component.radio.checkedFill', cssVar: '--spx-checkout-radio-checked-fill', group: 'component', type: 'color', default: '' },
  { key: 'component.radio.checkedIcon', cssVar: '--spx-checkout-radio-checked-icon', group: 'component', type: 'color', default: '#ffffff' },
  { key: 'component.divider.color', cssVar: '--spx-checkout-divider', group: 'component', type: 'color', default: 'rgba(255,255,255,0.06)' },
] as const satisfies readonly CheckoutStyleCssTokenDefinition[];

export const checkoutStyleAssetTokenDefinitions = [
  {
    key: 'brand.logoUrl',
    group: 'asset',
    type: 'asset_url',
    default: null,
    accept: ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'],
    maxBytes: 512_000,
  },
  {
    key: 'brand.logoDarkUrl',
    group: 'asset',
    type: 'asset_url',
    default: null,
    accept: ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'],
    maxBytes: 512_000,
  },
  {
    key: 'brand.logoMaxHeight',
    group: 'asset',
    type: 'number',
    default: 32,
    min: 16,
    max: 80,
    step: 2,
    unit: 'px',
  },
  {
    key: 'brand.faviconUrl',
    group: 'asset',
    type: 'asset_url',
    default: null,
    accept: ['image/png', 'image/x-icon', 'image/svg+xml'],
    maxBytes: 64_000,
  },
  {
    key: 'paymentLink.heroImageUrl',
    group: 'asset',
    type: 'asset_url',
    default: null,
    accept: ['image/png', 'image/jpeg', 'image/webp'],
    maxBytes: 1_048_576,
    surface: 'payment_link',
  },
  {
    key: 'paymentLink.heroPosition',
    group: 'asset',
    type: 'select',
    default: 'top',
    allowedValues: checkoutStylePaymentLinkHeroPositionValues,
    surface: 'payment_link',
  },
] as const satisfies readonly CheckoutStyleAssetTokenDefinition[];

export const checkoutStyleControlTokenDefinitions = [
  ...checkoutStyleTokenDefinitions,
  ...checkoutStyleAssetTokenDefinitions,
] as const satisfies readonly CheckoutStyleTokenDefinition[];

export type CheckoutStyleTokenKey = typeof checkoutStyleTokenDefinitions[number]['key'];
export type CheckoutStyleControlTokenKey = typeof checkoutStyleControlTokenDefinitions[number]['key'];
export type CheckoutStyleCssVariable = typeof checkoutStyleTokenDefinitions[number]['cssVar'];

export const embedStyleTokenDefinitions = [
  { key: 'embed.launcher.background', cssVar: '--spx-embed-launcher-background', group: 'embed', type: 'color', default: '#7c5cff' },
  { key: 'embed.launcher.text', cssVar: '--spx-embed-launcher-text', group: 'embed', type: 'color', default: '#ffffff', protected: true },
  { key: 'embed.launcher.radius', cssVar: '--spx-embed-launcher-radius', group: 'embed', type: 'number', default: 10, min: 0, max: 28, step: 1, unit: 'px' },
  { key: 'embed.launcher.paddingX', cssVar: '--spx-embed-launcher-padding-x', group: 'embed', type: 'number', default: 18, min: 8, max: 32, step: 1, unit: 'px' },
  { key: 'embed.launcher.paddingY', cssVar: '--spx-embed-launcher-padding-y', group: 'embed', type: 'number', default: 12, min: 6, max: 24, step: 1, unit: 'px' },
  { key: 'embed.launcher.shadow', cssVar: '--spx-embed-launcher-shadow', group: 'embed', type: 'shadow', default: '0 12px 32px rgba(0,0,0,0.18)' },
  { key: 'embed.productCard.background', cssVar: '--spx-embed-product-card-background', group: 'embed', type: 'color', default: '#ffffff' },
  { key: 'embed.productCard.border', cssVar: '--spx-embed-product-card-border', group: 'embed', type: 'color', default: 'rgba(24,24,27,0.12)' },
  { key: 'embed.productCard.imageRadius', cssVar: '--spx-embed-product-card-image-radius', group: 'embed', type: 'number', default: 8, min: 0, max: 24, step: 1, unit: 'px' },
  { key: 'embed.productCard.padding', cssVar: '--spx-embed-product-card-padding', group: 'embed', type: 'number', default: 16, min: 8, max: 32, step: 1, unit: 'px' },
  { key: 'embed.cart.background', cssVar: '--spx-embed-cart-background', group: 'embed', type: 'color', default: '#ffffff' },
  { key: 'embed.cart.rowBorder', cssVar: '--spx-embed-cart-row-border', group: 'embed', type: 'color', default: 'rgba(24,24,27,0.1)' },
  { key: 'embed.cart.itemSpacing', cssVar: '--spx-embed-cart-item-spacing', group: 'embed', type: 'number', default: 12, min: 4, max: 28, step: 1, unit: 'px' },
  { key: 'embed.modal.background', cssVar: '--spx-embed-modal-background', group: 'embed', type: 'color', default: '#ffffff' },
  { key: 'embed.modal.radius', cssVar: '--spx-embed-modal-radius', group: 'embed', type: 'number', default: 16, min: 0, max: 32, step: 1, unit: 'px' },
  { key: 'embed.modal.maxWidth', cssVar: '--spx-embed-modal-max-width', group: 'embed', type: 'number', default: 560, min: 360, max: 960, step: 20, unit: 'px' },
  { key: 'embed.modal.shadow', cssVar: '--spx-embed-modal-shadow', group: 'embed', type: 'shadow', default: '0 20px 60px rgba(0,0,0,0.2)' },
  { key: 'embed.backdrop.color', cssVar: '--spx-embed-backdrop-color', group: 'embed', type: 'color', default: 'rgba(0,0,0,0.6)' },
  { key: 'embed.backdrop.blur', cssVar: '--spx-embed-backdrop-blur', group: 'embed', type: 'number', default: 4, min: 0, max: 24, step: 1, unit: 'px' },
  { key: 'embed.backdrop.opacity', cssVar: '--spx-embed-backdrop-opacity', group: 'embed', type: 'number', default: 1, min: 0, max: 1, step: 0.05 },
  { key: 'embed.skeleton.background', cssVar: '--spx-embed-skeleton-background', group: 'embed', type: 'color', default: '#f4f4f5' },
  { key: 'embed.skeleton.shimmer', cssVar: '--spx-embed-skeleton-shimmer', group: 'embed', type: 'color', default: '#e8e8ec' },
  { key: 'embed.spinner.color', cssVar: '--spx-embed-spinner-color', group: 'embed', type: 'color', default: '#7c5cff' },
  {
    key: 'embed.closeButton.style',
    cssVar: '--spx-embed-close-button-style',
    group: 'embed',
    type: 'select',
    default: 'ghost',
    allowedValues: embedCloseButtonStyleValues,
  },
  {
    key: 'embed.mobile.layout',
    cssVar: '--spx-embed-mobile-layout',
    group: 'embed',
    type: 'select',
    default: 'sheet',
    allowedValues: embedMobileLayoutValues,
  },
  {
    key: 'embed.content.productDescription',
    cssVar: '--spx-embed-product-description-visibility',
    group: 'embed',
    type: 'select',
    default: 'visible',
    allowedValues: embedContentVisibilityValues,
  },
  {
    key: 'embed.content.termsShortcut',
    cssVar: '--spx-embed-terms-shortcut-visibility',
    group: 'embed',
    type: 'select',
    default: 'visible',
    allowedValues: embedContentVisibilityValues,
  },
] as const satisfies readonly CheckoutStyleCssTokenDefinition[];

export type EmbedStyleTokenKey = typeof embedStyleTokenDefinitions[number]['key'];
export type EmbedStyleCssVariable = typeof embedStyleTokenDefinitions[number]['cssVar'];

export type EmbedStyleCssVariableOptions = {
  mode?: CheckoutStyleMode;
};

/**
 * THE ALLOWLIST MERCHANT CUSTOM CSS IS VALIDATED AGAINST — by
 * `StyleThemeValidationService` on save, and by the dashboard's client-side
 * validator for the same verdict a keystroke earlier. It has two failure
 * directions and they are not symmetric:
 *
 *   a name MISSING while the element renders → the merchant writes a rule for
 *       a slot they can see in the DOM and the save answers 400. The slot sheet
 *       is the product surface; a hole in this list is a hole in the product.
 *   a name PRESENT while nothing renders it  → the rule is inert. Harmless.
 *
 * So this list tracks the markup one way only: every `data-spx-slot` the
 * checkout renders belongs here (pinned by
 * `apps/checkout/__tests__/checkout-style-slot-catalog.test.ts`), and a slot
 * that leaves the markup STAYS, because stored themes still target it and
 * dropping the name would fail every one of those saves.
 */
export const checkoutStyleSlotValues = [
  'checkout.shell',
  'checkout.chrome',
  'checkout.panel',
  'checkout.header',
  'checkout.footer',
  'checkout.trust',
  'checkout.section.active',
  'checkout.section.collapsed',
  'checkout.mobile_pay_bar',
  // The spacer under the bar, carrying the working column's ground so the last
  // scroll does not step down onto the shell's.
  'checkout.mobile_pay_bar_floor',
  'brand.logo',
  'brand.hero',
  // RETIRED, AND DELIBERATELY STILL HERE (see the note above). No element
  // carries `product.card` any more (see `component.productCard.*` above), so a
  // rule aimed at it is inert, which is the harmless half of the trade.
  'product.card',
  // What replaced it: the flat summary row. A merchant can reach it for type
  // and spacing; it has no card material to override, by design.
  'product.line',
  'product.image',
  'product.title',
  'product.description',
  'product.price',
  'product.quantity',
  'product.quantity_menu',
  'product.remove',
  'product.addon',
  // The "Included" marker a SELECTED add-on carries in place of its "+$9.00".
  // Its own slot rather than `product.price`, because it is not a figure: the
  // amount is already inside the line total above it, and a merchant styling
  // their price column must not accidentally style a word.
  'product.addon_included',
  // The line of glyph-led metadata under the product's title in the embed's
  // order bar — the description trigger plus whichever of the facts below the
  // product has. Its own slot so a merchant can retune the whole row's rhythm
  // (or hide it) without reaching into each fact one at a time.
  'product.facts',
  // How many are left. It appears in TWO places and deliberately shares one
  // slot: on the facts row when the product has no variants, and on each option
  // tile when it does — because with options there is no single stock figure,
  // only one per option. A merchant styling scarcity means the same thing in
  // both, and a shop is unlikely to have configured only the one it can see.
  'product.stock',
  'product.delivery',
  // The billing period a recurring line states — "/month" beside the figure,
  // or the full phrase on the metadata line when the period is not one-per-unit.
  'product.recurrence',
  'product.warranty',
  'paymentLink.hero',
  'summary.panel',
  'summary.line',
  'summary.total',
  // The row that CLOSES the ledger, distinct from `summary.total` (the column's
  // headline figure) and from `summary.line` (one part of the sum): it is the
  // sum itself, and it is the only row in the table that draws a rule.
  'summary.total_line',
  // What the total does next month, stated directly under it.
  'summary.recurrence',
  'summary.total_note',
  'summary.order_number',
  'summary.tracking',
  'summary.tip',
  'summary.after_payment',
  // The mobile order summary: a persistent bar that expands into the panel.
  'summary.mobile_bar',
  'summary.disclosure',
  'form.field',
  'form.label',
  'form.help',
  'form.value',
  'form.error',
  'form.panel',
  'form.save',
  'form.saved',
  'coupon.input',
  'coupon.applied',
  'input.base',
  'input.error',
  // The reserved line under an input that holds its message. It is present
  // whether or not there is something to say, which is the point: no reflow.
  'input.message_slot',
  'button.primary',
  'button.secondary',
  'button.blocked',
  'payment.methods',
  'payment.method',
  'payment.method.icon',
  'payment.method.label',
  'payment.method.meta',
  'payment.method.fee',
  'payment.method.indicator',
  'payment.method.trailing',
  'payment.method.change',
  'payment.selected_method',
  'payment.provider_widget',
  'payment.express_checkout',
  'payment.paypal_shell',
  'payment.acknowledgement',
  'payment.free_completion',
  'payment.notice',
  'payment.trial_notice',
  'payment.blocker_hint',
  'payment.loading',
  'payment.error',
  'payment.warning',
  'legal.terms',
  'status.success',
  'status.processing',
  'status.details',
  'status.wait_hint',
  // The open order kept its gateway: the buyer is told which one, and why the
  // picker is gone.
  'status.method_locked',
  'status.payment_rescue',
  'status.recovery',
  'status.delivery_destination',
  'embed.overlay_root',
  // The rest of the embed group belongs to the standalone widget bundle, which
  // no longer carries these names on an element. Kept for the same reason
  // `product.card` is kept: a stored theme that targets one must still save.
  'embed.launcher',
  'embed.launcher.icon',
  'embed.productCard',
  'embed.productCard.image',
  'embed.productCard.title',
  'embed.productCard.price',
  'embed.cart',
  'embed.cart.row',
  'embed.cart.summary',
  'embed.modal',
  'embed.modal.header',
  'embed.modal.close',
  'embed.skeleton',
  'embed.loader',
  'embed.branding',
] as const;

/**
 * Slots a merchant may restyle but may not make disappear — custom CSS on one
 * of these is rejected if it hides, zeroes, or unpins the element
 * (`blockedProtectedDeclarations` in `StyleThemeValidationService`).
 *
 * The test for membership is what the buyer loses: a way to pay, the price they
 * are agreeing to, or a disclosure we are obliged to show. Everything the 2026
 * rework added to the allowlist above was read against that test; the ones that
 * passed are here.
 */
export const checkoutStyleProtectedSlotValues = [
  'product.title',
  'product.price',
  'summary.total',
  'payment.method.label',
  'payment.provider_widget',
  // The pay control on mobile. It is the only one in the viewport once the
  // page scrolls, so hiding it hides checkout.
  'checkout.mobile_pay_bar',
  // Whole payment routes: express wallets, the PayPal button host, and the
  // completion control a zero-total order has instead of a gateway.
  'payment.express_checkout',
  'payment.paypal_shell',
  'payment.free_completion',
  // Disclosures. The acknowledgement is the buyer's consent to an offline
  // payment; the trial notice is what they will be charged and when.
  'payment.acknowledgement',
  'payment.trial_notice',
  'payment.loading',
  'payment.error',
  'payment.warning',
  'legal.terms',
  'status.success',
  'status.processing',
  'embed.modal.close',
  'embed.branding',
] as const;

export const CheckoutStyleSlotSchema = z.enum(checkoutStyleSlotValues);
export const CheckoutStyleProtectedSlotSchema = z.enum(checkoutStyleProtectedSlotValues);

export type CheckoutStyleSlot = z.infer<typeof CheckoutStyleSlotSchema>;
export type CheckoutStyleProtectedSlot = z.infer<typeof CheckoutStyleProtectedSlotSchema>;

const HexColorSchema = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Use a valid hex color');
// Permissive color schema for composite values (rgba, gradients, color-mix)
// used by token slots that aren't simple solid color picks. The CSS pipeline
// is the consumer; we just guard against unbounded length and obviously
// suspicious payloads (no semicolons, no curly braces, no @-rules).
const ExtendedColorSchema = z.string().trim().min(1).max(200).refine(
  (value) => !/[;{}]|@import|expression\s*\(/i.test(value),
  'Use a valid CSS color or gradient',
);
const ShadowSchema = z.string().trim().min(1).max(400).refine(
  (value) => !/[;{}]|@import|expression\s*\(/i.test(value),
  'Use a valid CSS box-shadow value',
);
// Curated font list — the families the Style Center dropdown offers. Every
// value here is served without a third-party request: the nine web families are
// bundled by `next/font/google` in `apps/checkout/app/layout.tsx`, and "Arial" /
// "System UI" come from the OS. A payment page must not hand the buyer's IP to
// fonts.googleapis.com, so a family added here MUST also be added to
// `app/layout.tsx` and to both maps in `apps/checkout/lib/checkout-style.tsx`
// (LOCALLY_LOADED_FONTS + LOCAL_FONT_FAMILY_TOKENS); the checkout font-stack
// test fails otherwise. Order matters: it drives both the dropdown in the
// editor and the type-checked union here.
export const curatedFontValues = [
  'Inter',
  'Geist',
  'Manrope',
  'Plus Jakarta Sans',
  'DM Sans',
  'Space Grotesk',
  'Sora',
  'IBM Plex Sans',
  'JetBrains Mono',
  'Arial',
  'System UI',
] as const;
const CuratedFontSchema = z.enum(curatedFontValues);

// Google Fonts family schema — accepts a single Google-hosted family name
// like "Source Serif 4" or "Crimson Pro". Restricted to the character set
// Google Fonts permits in family names (alphanumerics, spaces, +, -, _,
// digits) and capped at 60 chars to avoid abuse via giant CSS imports.
const GoogleFontFamilySchema = z
  .string()
  .trim()
  .min(1)
  .max(60)
  .regex(/^[A-Za-z0-9 +\-_]+$/, 'Use a Google Fonts family name');
const AssetUrlSchema = z.string().url();

export function isCheckoutStyleManagedAssetUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && checkoutStyleManagedAssetHostValues.includes(
      url.hostname as typeof checkoutStyleManagedAssetHostValues[number],
    );
  } catch {
    return false;
  }
}

export const CheckoutStyleTokensSchema = z.object({
  brand: z.object({
    logoUrl: AssetUrlSchema.optional().nullable(),
    logoDarkUrl: AssetUrlSchema.optional().nullable(),
    logoMaxHeight: z.number().int().min(16).max(80).optional(),
    faviconUrl: AssetUrlSchema.optional().nullable(),
  }).strict().optional(),
  color: z.object({
    brand: HexColorSchema.optional(),
    brandContrast: HexColorSchema.optional(),
    background: HexColorSchema.optional(),
    surface: HexColorSchema.optional(),
    surfaceRaised: HexColorSchema.optional(),
    text: HexColorSchema.optional(),
    textMuted: HexColorSchema.optional(),
    border: HexColorSchema.optional(),
    focus: HexColorSchema.optional(),
    success: HexColorSchema.optional(),
    warning: HexColorSchema.optional(),
    error: HexColorSchema.optional(),
  }).strict().optional(),
  typography: z.object({
    fontFamily: CuratedFontSchema.optional(),
    // Optional Google Fonts override. When set, the storefront injects the
    // fonts.googleapis.com stylesheet and uses this family in addition to
    // (or instead of) the curated fontFamily as the leftmost name in the
    // CSS font stack.
    googleFontFamily: GoogleFontFamilySchema.optional(),
    baseSize: z.number().int().min(12).max(18).optional(),
  }).strict().optional(),
  shape: z.object({
    buttonRadius: z.number().int().min(0).max(24).optional(),
    inputRadius: z.number().int().min(0).max(24).optional(),
    cardRadius: z.number().int().min(0).max(28).optional(),
  }).strict().optional(),
  spacing: z.object({
    density: CheckoutStyleDensitySchema.optional(),
    controlHeight: z.number().int().min(36).max(56).optional(),
  }).strict().optional(),
  component: z.object({
    primaryButton: z.object({
      background: HexColorSchema.optional(),
      text: HexColorSchema.optional(),
    }).strict().optional(),
    input: z.object({
      background: HexColorSchema.optional(),
      border: HexColorSchema.optional(),
      focusRing: ExtendedColorSchema.optional(),
    }).strict().optional(),
    summary: z.object({
      background: HexColorSchema.optional(),
    }).strict().optional(),
    paymentMethod: z.object({
      background: ExtendedColorSchema.optional(),
      cardBackground: ExtendedColorSchema.optional(),
      border: ExtendedColorSchema.optional(),
      selectedBackground: ExtendedColorSchema.optional(),
    }).strict().optional(),
    productCard: z.object({
      background: ExtendedColorSchema.optional(),
      border: ExtendedColorSchema.optional(),
      shadow: ShadowSchema.optional(),
    }).strict().optional(),
    productImage: z.object({
      background: ExtendedColorSchema.optional(),
      border: ExtendedColorSchema.optional(),
      icon: ExtendedColorSchema.optional(),
    }).strict().optional(),
    brandAvatar: z.object({
      background: ExtendedColorSchema.optional(),
      text: HexColorSchema.optional(),
      border: ExtendedColorSchema.optional(),
    }).strict().optional(),
    pill: z.object({
      background: ExtendedColorSchema.optional(),
      border: ExtendedColorSchema.optional(),
      text: HexColorSchema.optional(),
    }).strict().optional(),
    errorBanner: z.object({
      background: ExtendedColorSchema.optional(),
      border: ExtendedColorSchema.optional(),
      text: HexColorSchema.optional(),
    }).strict().optional(),
    warningBanner: z.object({
      background: ExtendedColorSchema.optional(),
      border: ExtendedColorSchema.optional(),
      text: HexColorSchema.optional(),
    }).strict().optional(),
    radio: z.object({
      idleRing: ExtendedColorSchema.optional(),
      checkedFill: HexColorSchema.optional(),
      checkedIcon: HexColorSchema.optional(),
    }).strict().optional(),
    divider: z.object({
      color: ExtendedColorSchema.optional(),
    }).strict().optional(),
  }).strict().optional(),
  paymentLink: z.object({
    heroImageUrl: AssetUrlSchema.optional().nullable(),
    heroPosition: CheckoutStylePaymentLinkHeroPositionSchema.optional(),
  }).strict().optional(),
  embed: z.object({
    launcher: z.object({
      background: ExtendedColorSchema.optional(),
      text: HexColorSchema.optional(),
      radius: z.number().int().min(0).max(28).optional(),
      paddingX: z.number().int().min(8).max(32).optional(),
      paddingY: z.number().int().min(6).max(24).optional(),
      shadow: ShadowSchema.optional(),
    }).strict().optional(),
    productCard: z.object({
      background: ExtendedColorSchema.optional(),
      border: ExtendedColorSchema.optional(),
      imageRadius: z.number().int().min(0).max(24).optional(),
      padding: z.number().int().min(8).max(32).optional(),
    }).strict().optional(),
    cart: z.object({
      background: ExtendedColorSchema.optional(),
      rowBorder: ExtendedColorSchema.optional(),
      itemSpacing: z.number().int().min(4).max(28).optional(),
    }).strict().optional(),
    modal: z.object({
      background: ExtendedColorSchema.optional(),
      radius: z.number().int().min(0).max(32).optional(),
      maxWidth: z.number().int().min(360).max(960).optional(),
      shadow: ShadowSchema.optional(),
    }).strict().optional(),
    backdrop: z.object({
      color: ExtendedColorSchema.optional(),
      blur: z.number().int().min(0).max(24).optional(),
      opacity: z.number().min(0).max(1).optional(),
    }).strict().optional(),
    skeleton: z.object({
      background: ExtendedColorSchema.optional(),
      shimmer: ExtendedColorSchema.optional(),
    }).strict().optional(),
    spinner: z.object({
      color: ExtendedColorSchema.optional(),
    }).strict().optional(),
    closeButton: z.object({
      style: EmbedCloseButtonStyleSchema.optional(),
    }).strict().optional(),
    mobile: z.object({
      layout: EmbedMobileLayoutSchema.optional(),
    }).strict().optional(),
    content: z.object({
      productDescription: EmbedContentVisibilitySchema.optional(),
      termsShortcut: EmbedContentVisibilitySchema.optional(),
    }).strict().optional(),
  }).strict().optional(),
}).strict();

export type CheckoutStyleTokens = z.infer<typeof CheckoutStyleTokensSchema>;

export const CheckoutStyleSettingsSchema = z.object({
  mode: CheckoutStyleModeSchema.default('system'),
  fontSource: z.enum(['curated']).default('curated'),
}).strict();

export type CheckoutStyleSettings = z.infer<typeof CheckoutStyleSettingsSchema>;

export const StyleThemeShareModeSchema = z.enum(['standalone', 'bundle', 'pointer']);
export type StyleThemeShareMode = z.infer<typeof StyleThemeShareModeSchema>;

export const StyleThemeParentHintSchema = z.object({
  name: z.string().trim().min(1).max(80),
  fingerprint: z.string().trim().min(1).max(128),
}).strict();

/**
 * Historic-data tolerance. Theme export codes are copy-pasted strings that live
 * outside this system — in merchant notes, Discord messages, and the
 * `style_theme_shares` table — so codes minted before the embed surface
 * collapsed to a single design still carry a top-level `embed_design` key (and
 * one inside `bundled_parent`). The export schemas are `.strict()`, so without
 * this strip every one of those codes would fail to import.
 *
 * Accept and ignore: the key is dropped before validation and never re-emitted.
 * Remove this once codes minted before the single-design release are no longer
 * expected to import (they carry no other retired field, so the strip can go
 * away wholesale).
 *
 * OBSERVABILITY LIVES ELSEWHERE, ON PURPOSE. This module is isomorphic — the
 * dashboard's import dialog parses the same code in the browser before the
 * backend ever sees it — so there is no logger to reach for here, and adding
 * one would put a server sink in a bundle that ships to merchants. The signal
 * that says whether the retirement has completed is the backend's
 * `retired_embed_design_key` warn line in
 * `apps/backend/src/services/style-center/embed-runtime.ts`, which watches the
 * same key on the transports and Redis sessions minted by the same release.
 * Those age out no earlier than a copy-pasted export code does, so a quiet
 * runtime is the precondition for dropping THIS strip too — never the proof on
 * its own. The removal plan for all three is one list:
 * `docs/architecture/domains/style-center.md` — Release 2.
 */
const RETIRED_EXPORT_FIELDS = ['embed_design'] as const;

function stripRetiredExportFields(value: unknown): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }

  const record = value as Record<string, unknown>;
  if (!RETIRED_EXPORT_FIELDS.some((field) => field in record)) {
    return value;
  }

  const cleaned = { ...record };
  for (const field of RETIRED_EXPORT_FIELDS) {
    delete cleaned[field];
  }
  return cleaned;
}

export const CheckoutStyleBundledParentThemeExportSchema = z.preprocess(
  stripRetiredExportFields,
  z.object({
    type: z.literal('shoppex.style_theme'),
    surface: z.literal('checkout'),
    format_version: z.union([
      z.literal(1),
      z.literal(SHOPPEX_STYLE_THEME_EXPORT_FORMAT_VERSION),
    ]),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(240).optional(),
    token_schema_version: z.literal(CHECKOUT_STYLE_TOKEN_SCHEMA_VERSION),
    tokens: CheckoutStyleTokensSchema.default({}),
    custom_css: z.string().max(20_000).default(''),
    settings: CheckoutStyleSettingsSchema.default({ mode: 'system', fontSource: 'curated' }),
    _meta: z.record(z.string(), z.unknown()).optional(),
  }).strict(),
);

export const CheckoutStyleThemeExportSchema = z.preprocess(
  stripRetiredExportFields,
  z.object({
    type: z.literal('shoppex.style_theme'),
    surface: CheckoutStyleSurfaceSchema,
    format_version: z.union([
      z.literal(1),
      z.literal(SHOPPEX_STYLE_THEME_EXPORT_FORMAT_VERSION),
    ]),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(240).optional(),
    token_schema_version: z.literal(CHECKOUT_STYLE_TOKEN_SCHEMA_VERSION),
    tokens: CheckoutStyleTokensSchema.default({}),
    custom_css: z.string().max(20_000).default(''),
    settings: CheckoutStyleSettingsSchema.default({ mode: 'system', fontSource: 'curated' }),
    share_mode: StyleThemeShareModeSchema.default('standalone'),
    parent_hint: StyleThemeParentHintSchema.optional(),
    bundled_parent: CheckoutStyleBundledParentThemeExportSchema.optional(),
    _meta: z.record(z.string(), z.unknown()).optional(),
  }).strict(),
);

export type CheckoutStyleThemeExport = z.infer<typeof CheckoutStyleThemeExportSchema>;

export const ResolvedCheckoutStyleSchema = z.object({
  theme_id: z.string().uuid().nullable(),
  surface: CheckoutStyleSurfaceSchema,
  token_schema_version: z.literal(CHECKOUT_STYLE_TOKEN_SCHEMA_VERSION),
  revision: z.number().int().min(1),
  tokens: CheckoutStyleTokensSchema,
  custom_css: z.string(),
  css_variables: z.record(z.string(), z.string()),
}).strict();

export type ResolvedCheckoutStyle = z.infer<typeof ResolvedCheckoutStyleSchema>;

export const ResolvedEmbedStyleSchema = z.object({
  theme_id: z.string().uuid().nullable(),
  parent_theme_id: z.string().uuid().nullable(),
  surface: z.literal('embed'),
  token_schema_version: z.literal(CHECKOUT_STYLE_TOKEN_SCHEMA_VERSION),
  revision: z.number().int().min(1),
  parent_revision: z.number().int().min(1).nullable(),
  tokens: CheckoutStyleTokensSchema,
  custom_css: z.string(),
  css_variables: z.record(z.string(), z.string()),
}).strict();

export type ResolvedEmbedStyle = z.infer<typeof ResolvedEmbedStyleSchema>;

/** Canonical embed runtime contract shared by SDK shell and checkout iframe. */
export const EmbedRuntimeStyleSchema = ResolvedEmbedStyleSchema;
export type EmbedRuntimeStyle = ResolvedEmbedStyle;

export const EmbedRuntimeSessionContextSchema = z.object({
  shop_id: z.string().uuid(),
  product_id: z.string().uuid().nullable(),
  product_group_id: z.string().uuid().nullable(),
  payment_link_id: z.string().uuid().nullable(),
  invoice_id: z.string().nullable(),
}).strict();

export type EmbedRuntimeSessionContext = z.infer<typeof EmbedRuntimeSessionContextSchema>;

export const EmbedRuntimeThemeResponseSchema = z.object({
  embed_runtime: z.string().min(16).max(128),
  revision: z.number().int().min(1),
  parent_revision: z.number().int().min(1).nullable(),
  theme_id: z.string().uuid().nullable(),
  css_variables: z.record(z.string(), z.string()),
  custom_css: z.string(),
}).strict();

export type EmbedRuntimeThemeResponse = z.infer<typeof EmbedRuntimeThemeResponseSchema>;

export const EmbedRuntimeTransportSchema = z.object({
  embed_style_revision: z.coerce.number().int().min(1).optional(),
  embed_runtime: z.string().min(16).max(128).optional(),
  preview: z.boolean().optional(),
}).strict();

export type EmbedRuntimeTransport = z.infer<typeof EmbedRuntimeTransportSchema>;

function readPath(input: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((current, segment) => {
    if (!current || typeof current !== 'object' || Array.isArray(current)) return undefined;
    return (current as Record<string, unknown>)[segment];
  }, input);
}

function formatCssValue(definition: CheckoutStyleTokenDefinition, value: string | number): string {
  if (definition.type === 'number' && definition.unit) {
    return `${value}${definition.unit}`;
  }

  // Multi-word font family names must be quoted so they survive var()
  // substitution into a font-family list. Single-word names like "Inter"
  // or generic keywords are left bare so they keep their generic-family
  // semantics.
  if (definition.type === 'font' && typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) return '';
    if (/\s/.test(trimmed) && !/^['"]/.test(trimmed)) {
      return `"${trimmed.replace(/"/g, '\\"')}"`;
    }
    return trimmed;
  }

  return String(value);
}

export function resolveCheckoutStyleTokenValue(
  tokens: CheckoutStyleTokens,
  key: CheckoutStyleTokenKey,
  options: { parentTokens?: CheckoutStyleTokens } = {},
): string | number | undefined {
  const own = readPath(tokens, key);
  if (isExplicitCheckoutStyleTokenValue(own)) {
    return own as string | number;
  }

  const parent = options.parentTokens ? readPath(options.parentTokens, key) : undefined;
  if (isExplicitCheckoutStyleTokenValue(parent)) {
    return parent as string | number;
  }

  const brandDerivedKey = resolveCheckoutStyleBrandDerivedTokenKey(key);
  if (brandDerivedKey) {
    const brand = resolveCheckoutStyleTokenValue(tokens, brandDerivedKey as CheckoutStyleTokenKey, options);
    if (isExplicitCheckoutStyleTokenValue(brand)) {
      return brand;
    }
  }

  const definition = checkoutStyleTokenDefinitions.find((entry) => entry.key === key);
  if (definition && isExplicitCheckoutStyleTokenValue(definition.default)) {
    return definition.default;
  }

  const baseline = CHECKOUT_PLATFORM_BASELINE[key];
  return baseline !== undefined ? baseline : undefined;
}

export function createCheckoutStyleCssVariables(tokens: CheckoutStyleTokens): Record<CheckoutStyleCssVariable, string> {
  const variables = {} as Record<CheckoutStyleCssVariable, string>;

  for (const definition of checkoutStyleTokenDefinitions) {
    // A deprecated token is still ACCEPTED by the schema (stored themes must
    // keep saving) but is no longer PAINTED: nothing reads its variable any
    // more, and emitting it would leave a dead declaration on every checkout
    // that a later reader could mistake for a live hook.
    if (isDeprecatedCheckoutStyleToken(definition)) {
      continue;
    }
    const value = readPath(tokens, definition.key);
    if (!isExplicitCheckoutStyleTokenValue(value)) {
      continue;
    }
    variables[definition.cssVar] = formatCssValue(definition, value);
  }

  return variables;
}

/**
 * Token groups whose defaults are safe to materialise into the live checkout
 * root. These are the "foundation" tokens (brand, base colors, shape, spacing)
 * that sit at the INNER end of the system-CSS `var()` fallback chains —
 * e.g. `var(--spx-checkout-product-card-border, var(--spx-checkout-border, …))`.
 * Materialising them kills the white-`currentColor` fallback bug without
 * touching the cascade.
 *
 * The `component` group is deliberately excluded: those tokens are the OUTER
 * end of the chains, so emitting their defaults (e.g. `--spx-checkout-product-card-bg`)
 * would shadow base-token edits — a merchant who only customises `color.surfaceRaised`
 * would no longer see it cascade into product cards. Their defaults stay inline
 * in the system CSS as the final hex fallback instead.
 *
 * The `typography` group is also excluded: `typography.fontFamily` resolves to a
 * bare family name ("Geist") that no `@font-face` declares — the checkout loads
 * Geist via `next/font` as `--font-geist-sans`. Materialising `--spx-checkout-font`
 * as "Geist" would make the font-family rule bypass the Next webfont and fall
 * back to system sans. The system CSS keeps CHECKOUT_PLATFORM_FONT_STACK (which
 * references `--font-geist-sans`) as its inline default instead.
 */
const CHECKOUT_BASELINE_TOKEN_GROUPS = new Set(['brand', 'color', 'shape', 'spacing']);

/**
 * Like {@link createCheckoutStyleCssVariables}, but materialises each foundation
 * token's SSOT default when the merchant hasn't set an explicit value. The live
 * hosted checkout renders this so the base `--spx-checkout-*` variables are
 * always present in the DOM, which means the system CSS never falls back to
 * `currentColor` (that fallback resolved to the white text colour → white
 * borders/buttons for any merchant who never opened the Style Center).
 *
 * Rules:
 * - Explicit merchant values are always emitted (any group, including component
 *   overrides), so partial Style Center themes keep working.
 * - Unset tokens only get their default emitted when they belong to a foundation
 *   group (see {@link CHECKOUT_BASELINE_TOKEN_GROUPS}); component defaults are
 *   left to the inline system-CSS fallbacks so base-token edits cascade.
 * - Empty defaults (`''`) are skipped so the value stays unset — most importantly
 *   `color.background` (`--spx-checkout-bg`), which must remain absent so the
 *   two hosted columns keep their own rungs of the ladder (`--surface-0` for the
 *   working panel, `--surface-1` for the summary ground). A materialised
 *   background paints BOTH and the column split disappears. This resolves only
 *   to `definition.default`, NOT through `resolveCheckoutStyleTokenValue`,
 *   because that resolver would fall through to the opaque
 *   `CHECKOUT_PLATFORM_BASELINE` background.
 */
/**
 * Light-appearance defaults for the baseline foundation tokens. Keys not listed
 * here keep their (appearance-neutral) definition default. `color.background`
 * stays absent in both appearances so the column split and the summary column's
 * own ground survive.
 *
 * Same rule as the dark defaults above: every value is the LIGHT rung of the
 * ladder in `apps/checkout/app/globals.css` — `--surface-0`, `--card`,
 * `--foreground`, `--muted-foreground`, `--border` — so that a shop with no
 * theme renders exactly what the components paint.
 */
export const CHECKOUT_PLATFORM_BASELINE_LIGHT_DEFAULTS: Partial<Record<string, string>> = {
  // `--surface-0`: the working panel, PURE WHITE. It is the form half of the
  // page, and a form's structure comes from the edge of each field and tile,
  // not from the tone of the sheet they sit on. Two tinted panels shipped here
  // before this one (#f7f7f7, then a #f0f0f0 "well") and both made the middle
  // of the page read as a tonal step rather than as a join.
  'color.surface': '#ffffff',
  // `--surface-2`: a card resting on that panel — the SAME white, on purpose.
  // Light separates a card from its panel by `--card-hairline` (#e8e8e8) and a
  // whisper of `--card-shadow`, never by a step of fill. That these two are
  // equal is the model, not a missing value: the summary column (#fafafa) is
  // the only tonal step the light theme spends anywhere.
  'color.surfaceRaised': '#ffffff',
  'color.text': '#101010',
  'color.textMuted': '#636363',
  // `--border`. It is the pair that matters, not either value: a shop on the
  // platform baseline has to get the same relation between a field edge and a
  // card edge that the checkout paints for everyone else. Both halves moved up
  // together to Stripe Checkout's measured pair (card edge #e8e8e8, input ring
  // #e0e0e0) when the working panel's white-on-white cards turned out to have
  // nothing but this line holding them off the sheet.
  // #e4e4e4 is the value `--border-hairline` / `--card-hairline` settled on in
  // apps/checkout/app/globals.css; the baseline follows it so a shop that never
  // opened the Style Center gets the same edge the checkout paints for everyone.
  'color.border': '#e4e4e4',
  'color.success': '#16a34a',
  'color.warning': '#ca8a04',
  'color.error': '#dc2626',
};

export type CheckoutStyleAppearance = 'dark' | 'light';

export function createCheckoutStyleBaselineCssVariables(
  tokens: CheckoutStyleTokens,
  appearance: CheckoutStyleAppearance = 'dark',
): Record<CheckoutStyleCssVariable, string> {
  const variables = {} as Record<CheckoutStyleCssVariable, string>;

  for (const definition of checkoutStyleTokenDefinitions) {
    if (isDeprecatedCheckoutStyleToken(definition)) {
      continue;
    }
    const own = readPath(tokens, definition.key);
    if (isExplicitCheckoutStyleTokenValue(own)) {
      variables[definition.cssVar] = formatCssValue(definition, own);
      continue;
    }
    if (!CHECKOUT_BASELINE_TOKEN_GROUPS.has(definition.group)) {
      continue;
    }
    const baselineDefault = appearance === 'light'
      ? CHECKOUT_PLATFORM_BASELINE_LIGHT_DEFAULTS[definition.key] ?? definition.default
      : definition.default;
    if (!isExplicitCheckoutStyleTokenValue(baselineDefault)) {
      continue;
    }
    variables[definition.cssVar] = formatCssValue(definition, baselineDefault);
  }

  return variables;
}

/**
 * Dark-appearance defaults for the embed chrome tokens. The `definition.default`
 * of every embed token is the LIGHT value, so only the keys that actually differ
 * in dark are listed here. Emitted only when the theme's mode is explicitly
 * 'dark' — never inferred from anything else.
 */
const EMBED_STYLE_DARK_DEFAULTS: Partial<Record<EmbedStyleTokenKey, string>> = {
  'embed.productCard.background': '#09090b',
  'embed.productCard.border': 'rgba(255,255,255,0.08)',
  'embed.cart.background': '#09090b',
  'embed.cart.rowBorder': '#27272a',
  'embed.modal.background': '#0a0a0c',
  'embed.modal.shadow': '0 20px 60px rgba(0,0,0,0.45)',
  'embed.skeleton.background': '#1a1a1e',
  'embed.skeleton.shimmer': '#2a2a2f',
};

/**
 * Emits the `--spx-embed-*` variables for a resolved embed style.
 *
 * Rules:
 * - An explicit merchant token always wins and is always emitted.
 * - An unset token gets a baked default ONLY when the theme picked an
 *   appearance explicitly (`settings.mode` of 'dark' or 'light').
 * - Mode 'system' (and an unset mode) bakes nothing. The embed SDK stylesheet
 *   reads every one of these through `var(--spx-embed-*, <fallback>)` and pairs
 *   that with its own `prefers-color-scheme` handling (see
 *   `shouldUseDarkShell` in apps/checkout/src/embed/modal.ts, which reads
 *   `--spx-checkout-color-mode` first and falls back to the media query).
 *   Baking an appearance here would pin a merchant who asked for "follow the
 *   visitor's system setting" to one of the two.
 */
export function createEmbedStyleCssVariables(
  tokens: CheckoutStyleTokens,
  options: EmbedStyleCssVariableOptions = {},
): Record<EmbedStyleCssVariable, string> {
  const variables = {} as Record<EmbedStyleCssVariable, string>;
  const bakeDefaults = options.mode === 'dark' || options.mode === 'light';

  for (const definition of embedStyleTokenDefinitions) {
    const value = readPath(tokens, definition.key);

    if (typeof value === 'string' || typeof value === 'number') {
      if (typeof value === 'string' && value.length === 0) {
        continue;
      }
      variables[definition.cssVar] = formatCssValue(definition, value);
      continue;
    }

    if (!bakeDefaults) {
      continue;
    }

    const resolvedDefault = options.mode === 'dark'
      ? EMBED_STYLE_DARK_DEFAULTS[definition.key] ?? definition.default
      : definition.default;
    if (typeof resolvedDefault === 'string' && resolvedDefault.length === 0) {
      continue;
    }
    variables[definition.cssVar] = formatCssValue(definition, resolvedDefault);
  }

  return variables;
}
